// Wallet ledger + order-payment reconciliation + month/period profit math.
// This is the "wallet reconciliation" logic the original refactor spec
// singled out for protection (marking an order received must create
// exactly one transaction; re-marking must update it, not duplicate it).
//
// Extracted from public/index.html's inline script (Phase 2 modularization,
// slice 4). Same dependency-injection + thin-wrapper pattern as
// pricing.js/inventory.js — every function here takes the data it needs
// (orders, products, wallet accounts/transactions, the current filter) as
// an explicit argument instead of closing over the app's mutable globals;
// public/index.html keeps a one-line wrapper of the same original name for
// each, so every existing call site keeps working unchanged. See
// REFACTOR_SECURITY_REPORT.md, Phase 2 section, for the before/after
// behavioral-equivalence checks run before each function was removed from
// the inline script.
import { curMonth, monthKey, todayISO } from '../utils/format.js';
import { norm } from '../utils/text.js';
import { deliveryCost, itemCost, orderTotal, unitCostOf } from './pricing.js';

// ---------- order-payment normalization ----------

export function legacyPaymentMethod(o) {
  const p = norm(o && o.payment);
  if (p.includes('virement') || p.includes('avance')) return 'bank_transfer';
  if (p.includes('espec')) return 'cash';
  return 'cod';
}

export function ensureOrderPayment(o) {
  if (!o) return o;
  if (o.company === 'Moi-même') { o.deliveryFee = 0; o.paymentMethod = 'cash'; o.payment = 'Espèces'; }
  else if (!o.paymentMethod) o.paymentMethod = legacyPaymentMethod(o);
  if (!o.paymentStatus) { o.paymentStatus = (o.company === 'Moi-même' && o.status === 'livree') ? 'received' : (o.status === 'livree' ? 'pending' : 'unpaid'); }
  if (o.company === 'Moi-même' && o.status === 'livree') o.paymentStatus = 'received';
  if (o.paymentAccount == null) o.paymentAccount = o.paymentMethod === 'cash' ? 'cash' : '';
  if (o.company === 'Moi-même') o.paymentAccount = 'cash';
  if (!o.paymentReceivedAt && o.paymentStatus === 'received') o.paymentReceivedAt = o.deliverDate || o.orderDate || todayISO();
  if (o.paymentReference == null) o.paymentReference = '';
  return o;
}

export function orderPaymentDate(o) {
  return o.paymentReceivedAt || o.deliverDate || o.orderDate;
}

// The amount that actually lands in the account for a given order — NOT
// always the same as orderTotal(). For "cod" ("Paiement au livreur") orders
// the courier collects cash from the customer and remits it to the shop
// only after deducting its own delivery fee, so the shop never receives the
// full order total, only orderTotal − deliveryFee. For bank_transfer/cash
// payments no courier is handling the money, so the shop receives the full
// order total; the delivery fee there is instead a separate out-of-pocket
// cost paid directly (already accounted for as an expense via
// deliveryCost() in profitForRange()/monthStats()'s `ship` term, applied
// uniformly regardless of payment method — that accrual math needed no
// change, only the actual cash-in-hand tracked here did).
export function netReceivedAmount(products, o) {
  const total = orderTotal(products, o);
  return o && o.paymentMethod === 'cod' ? Math.max(0, total - deliveryCost(o)) : total;
}

// Mutates `walletTransactions` in place (splice/unshift), matching the
// original's mutation of SETTINGS.walletTransactions — callers pass
// SETTINGS.walletTransactions itself, and `txid`/`user` as explicit args
// since this module doesn't import the app's ID generator or read
// SETTINGS.user directly.
export function syncOrderPaymentTransaction(o, products, walletTransactions, txid, user) {
  ensureOrderPayment(o);
  const idx = walletTransactions.findIndex(t => t.orderId === o.id && t.category === 'order');
  if (o.paymentStatus !== 'received') { if (idx >= 0) walletTransactions.splice(idx, 1); return; }
  const tx = {
    id: idx >= 0 ? walletTransactions[idx].id : txid(), type: 'income', category: 'order', amount: netReceivedAmount(products, o),
    date: orderPaymentDate(o) || todayISO(), accountId: o.paymentAccount || '',
    source: o.paymentMethod === 'cod' ? (o.company || 'Livreur') : (o.paymentMethod === 'bank_transfer' ? 'Virement client' : 'Paiement espèces'),
    orderId: o.id, orderNo: o.orderNo, note: o.paymentReference || ('Paiement ' + (o.orderNo || '')),
    createdAt: idx >= 0 ? walletTransactions[idx].createdAt : new Date().toISOString(), user: user || 'Admin'
  };
  if (idx >= 0) walletTransactions[idx] = tx; else walletTransactions.unshift(tx);
}

// ---------- ledger reads ----------

export function walletAccount(walletAccounts, id) {
  return (walletAccounts || []).find(a => a.id === id);
}

export function walletAccountName(walletAccounts, id) {
  return (walletAccount(walletAccounts, id) || {}).name || (id ? 'Compte inconnu' : 'À affecter');
}

export function accountBalance(walletAccounts, walletTransactions, id) {
  return Number((walletAccount(walletAccounts, id) || {}).openingBalance || 0)
    + walletTransactions.filter(t => t.accountId === id).reduce((s, t) => s + (t.type === 'income' ? 1 : -1) * Number(t.amount || 0), 0);
}

// ---------- date-range filtering ----------

export function dateLocal(v) {
  const p = String(v || todayISO()).slice(0, 10).split('-').map(Number);
  return new Date(p[0], Math.max(0, p[1] - 1), p[2] || 1);
}

export function isoLocal(d) {
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function walletRange(filter) {
  const p = filter.period, a = dateLocal(filter.anchor || todayISO());
  let from, to;
  if (p === 'week') { const day = (a.getDay() + 6) % 7; from = addDays(a, -day); to = addDays(from, 6); }
  else if (p === 'quarter') { const m = Math.floor(a.getMonth() / 3) * 3; from = new Date(a.getFullYear(), m, 1); to = new Date(a.getFullYear(), m + 3, 0); }
  else if (p === 'year') { from = new Date(a.getFullYear(), 0, 1); to = new Date(a.getFullYear(), 11, 31); }
  else if (p === 'custom') { from = dateLocal(filter.from || todayISO()); to = dateLocal(filter.to || filter.from || todayISO()); if (from > to) { const x = from; from = to; to = x; } }
  else { from = new Date(a.getFullYear(), a.getMonth(), 1); to = new Date(a.getFullYear(), a.getMonth() + 1, 0); }
  return { from: isoLocal(from), to: isoLocal(to) };
}

export function inRange(date, range) {
  const d = String(date || '').slice(0, 10);
  return !!d && d >= range.from && d <= range.to;
}

export function selectedWalletTransactions(filter, walletTransactions) {
  const r = walletRange(filter);
  return walletTransactions.filter(t => inRange(t.date, r));
}

// ---------- month/period profit ----------

export function monthOf(o) {
  return monthKey(o.deliverDate || o.orderDate || o.createdAt);
}

// `packagingCostForOrder` is passed in as a callback rather than a value: it
// depends on SETTINGS/ensureInventorySettings (not yet extracted — see
// REFACTOR_SECURITY_REPORT.md), and the original calls it once per
// delivered order (not once total) — preserved exactly as-is here, not
// "fixed", since changing that would change real financial output.
export function monthStats(m, orders, products, walletTransactions, packagingCostForOrder) {
  const del = orders.filter(o => o.status === 'livree' && monthOf(o) === m);
  const revenue = del.reduce((s, o) => s + orderTotal(products, o), 0);
  const cogs = del.reduce((s, o) => s + unitCostOf(products, o), 0);
  const pack = del.reduce((s) => s + packagingCostForOrder(), 0);
  const ship = del.reduce((s, o) => s + deliveryCost(o), 0);
  const ads = walletTransactions.filter(t => t.type === 'expense' && t.category === 'ads' && monthKey(t.date) === m).reduce((s, t) => s + Number(t.amount || 0), 0);
  const operating = walletTransactions.filter(t => t.type === 'expense' && ['fees', 'other'].includes(t.category) && monthKey(t.date) === m).reduce((s, t) => s + Number(t.amount || 0), 0);
  const net = revenue - cogs - pack - ship - ads - operating;
  return { m, count: del.length, revenue, cogs, pack, ship, ads, operating, net, marginBeforeAds: revenue - cogs - pack - ship };
}

export function allMonths(orders, settingsAds) {
  const set = new Set();
  orders.forEach(o => { if (o.status === 'livree') set.add(monthOf(o)); });
  Object.keys(settingsAds || {}).forEach(m => set.add(m));
  set.add(curMonth());
  return [...set].filter(Boolean).sort().reverse();
}

export function profitForRange(range, orders, products, walletTransactions, packagingCostForOrder) {
  const delivered = orders.filter(o => o.status === 'livree' && inRange(o.deliverDate || o.orderDate, range));
  const revenue = delivered.reduce((s, o) => s + orderTotal(products, o), 0);
  const cogs = delivered.reduce((s, o) => s + itemCost(products, o), 0);
  const pack = delivered.reduce((s) => s + packagingCostForOrder(), 0);
  const delivery = delivered.reduce((s, o) => s + deliveryCost(o), 0);
  const expenses = walletTransactions.filter(t => t.type === 'expense' && inRange(t.date, range));
  const ads = expenses.filter(t => t.category === 'ads').reduce((s, t) => s + Number(t.amount || 0), 0);
  const operating = expenses.filter(t => ['fees', 'other'].includes(t.category)).reduce((s, t) => s + Number(t.amount || 0), 0);
  return { revenue, cogs, pack, delivery, ads, operating, net: revenue - cogs - pack - delivery - ads - operating, count: delivered.length };
}
